/**
 * Close a "Manual Agreement" schedule line — customer side only.
 *
 * A collab-held line is skipped by every ERP writer, including the auto-close
 * reconcile, so when the ERP never confirms the agreed change its remainder
 * can only be closed by hand. This dialog states exactly what the close does
 * (balance to zero, receipts kept, hold released), takes a mandatory reason
 * and calls `POST /schedules/{id}/close-held/`. Shared by the Scheduled Lines
 * row and the GRN Repair card so both use one codepath.
 *
 * Callers gate rendering on the viewer being customer-side; the backend
 * enforces the same rule.
 */
import { useEffect, useState } from 'react';
import { Loader2, CircleSlash } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { purchasingAPI } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { describeManualAgreement, type ManualAgreementTarget } from './ManualAgreementBadge';

/** Minimal slice of POLineSchedule the dialog needs. */
export interface CloseHeldTarget extends ManualAgreementTarget {
  id: number;
  po_number?: string;
  line_number?: number;
  schedule_line_number?: string;
  part_number?: string;
  schedule_qty?: string;
  shipped_qty?: string;
  balance_qty?: string;
}

interface CloseHeldScheduleDialogProps {
  open: boolean;
  schedule: CloseHeldTarget | null;
  onClose: () => void;
  /** Called after a successful close so the parent can refetch. */
  onDone: () => void;
}

export default function CloseHeldScheduleDialog({ open, schedule, onClose, onDone }: CloseHeldScheduleDialogProps) {
  const { formatDate, formatNumber } = useFormatters();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open, schedule?.id]);

  if (!schedule) return null;

  const shipped = parseFloat(schedule.shipped_qty ?? '0') || 0;
  const balance = parseFloat(schedule.balance_qty ?? '0') || 0;
  const qty = parseFloat(schedule.schedule_qty ?? '0') || 0;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required to close this line.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await purchasingAPI.closeHeldSchedule(schedule.id, { reason: reason.trim() });
      toast.success(res.data.detail || 'Schedule line closed; receipts kept.');
      onDone();
      onClose();
    } catch (err: unknown) {
      console.error('Close held schedule failed:', err);
      const axiosError = err as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(
        axiosError.response?.data?.error ||
          axiosError.response?.data?.detail ||
          'Failed to close the schedule line',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const ref = [
    schedule.po_number,
    schedule.line_number != null ? `Line ${schedule.line_number}` : null,
    schedule.schedule_line_number ? `Sched ${schedule.schedule_line_number}` : null,
  ].filter(Boolean).join(' / ');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleSlash className="h-5 w-5 text-violet-700" />
            Close Manually Agreed Line
          </DialogTitle>
          <DialogDescription>
            {ref}{schedule.part_number ? <> · <span className="font-mono">{schedule.part_number}</span></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{describeManualAgreement(schedule, formatDate)}</p>
          <div className="rounded-md border bg-slate-50 p-3 text-sm space-y-1">
            <p><span className="text-muted-foreground">Scheduled:</span> {formatNumber(qty)}</p>
            <p><span className="text-muted-foreground">Received (kept):</span> {formatNumber(shipped)}</p>
            <p>
              <span className="text-muted-foreground">Open balance:</span>{' '}
              <span className="font-medium text-orange-600">{formatNumber(balance)}</span>
              <span className="text-muted-foreground"> → 0</span>
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            The line is marked <span className="font-semibold">Closed</span>, drops out of arrears and open
            counts, and the ERP will not reopen it. Who, when and why are recorded in the audit history.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="close-held-reason">Reason <span className="text-red-500">*</span></Label>
            <Textarea
              id="close-held-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. ERP re-planned the remaining demand on a later slot"
              rows={3}
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} type="button">Cancel</Button>
          <Button
            className="bg-violet-700 hover:bg-violet-800 text-white"
            onClick={submit}
            disabled={submitting || !reason.trim()}
            type="button"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CircleSlash className="h-4 w-4 mr-2" />}
            Close Line
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
